import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  sent: { bg: "#E7F0EA", color: "#3F6B4E", label: "Sent" },
  scheduled: { bg: "#FBF0DA", color: "#8A6D1F", label: "Scheduled" },
  sending: { bg: "#E4EDF5", color: "#3A5A7A", label: "Sending…" },
  cancelled: { bg: "#F0ECE9", color: "#8A817B", label: "Cancelled" },
};

export default function EmailComposer({ users }) {
  const [recipientType, setRecipientType] = useState("customers");
  const [recipientId, setRecipientId] = useState("");
  const [customEmails, setCustomEmails] = useState("");
  const [subject, setSubject] = useState("");
  const [heading, setHeading] = useState("");
  const [message, setMessage] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaLink, setCtaLink] = useState("");
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledFor, setScheduledFor] = useState("");
  const [sending, setSending] = useState(false);

  const [preview, setPreview] = useState(null); // {html, count}
  const [previewLoading, setPreviewLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/admin/email-history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const people = users.filter((u) => u.role === "customer" || u.role === "trade");
  const groupCount = {
    customers: users.filter((u) => u.role === "customer").length,
    trades: users.filter((u) => u.role === "trade").length,
    everyone: people.length,
  };

  const payload = (extra = {}) => ({
    subject: subject.trim(),
    heading: heading.trim() || null,
    message,
    cta_label: ctaLabel.trim() || null,
    cta_link: ctaLink.trim() || null,
    recipient_type: recipientType,
    recipient_id: recipientType === "single" ? recipientId : null,
    custom_emails: recipientType === "custom" ? customEmails : null,
    ...extra,
  });

  const customCount = customEmails.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean).length;

  const validate = () => {
    if (!subject.trim()) { toast.error("Add a subject line."); return false; }
    if (!message.trim()) { toast.error("Write a message."); return false; }
    if (recipientType === "single" && !recipientId) { toast.error("Choose a recipient."); return false; }
    if (recipientType === "custom" && customCount === 0) { toast.error("Type at least one email address."); return false; }
    if ((ctaLabel.trim() && !ctaLink.trim()) || (!ctaLabel.trim() && ctaLink.trim())) {
      toast.error("A button needs both a label and a link."); return false;
    }
    if (scheduleMode && !scheduledFor) { toast.error("Pick a date and time to schedule."); return false; }
    return true;
  };

  const openPreview = async () => {
    if (recipientType === "single" && !recipientId) { toast.error("Choose a recipient to preview the count."); return; }
    if (recipientType === "custom" && customCount === 0) { toast.error("Type at least one email address to preview."); return; }
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/admin/broadcast-email/preview", payload());
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not build preview"); }
    finally { setPreviewLoading(false); }
  };

  const sendTest = async () => {
    if (!validate()) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast-email", payload({ test_only: true }));
      toast.success(data.sent ? `Test sent to ${data.to}.` : "Could not send test — check email setup.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send test"); }
    finally { setSending(false); }
  };

  const resetForm = () => {
    setSubject(""); setHeading(""); setMessage(""); setCtaLabel(""); setCtaLink("");
    setScheduleMode(false); setScheduledFor(""); setCustomEmails("");
  };

  const sendReal = async () => {
    if (!validate()) return;
    const audience = recipientType === "single"
      ? (people.find((p) => p.id === recipientId)?.name || "this recipient")
      : recipientType === "custom"
        ? `${customCount} typed ${customCount === 1 ? "address" : "addresses"}`
        : `${groupCount[recipientType]} ${recipientType === "everyone" ? "people" : recipientType}`;
    const when = scheduleMode
      ? `at ${new Date(scheduledFor).toLocaleString("en-GB")} (UK time)`
      : "right now";
    if (!window.confirm(`Send "${subject.trim()}" to ${audience} ${when}? This emails real inboxes.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast-email", payload(scheduleMode ? { scheduled_for: scheduledFor } : {}));
      if (data.scheduled) {
        toast.success(`Scheduled for ${new Date(data.scheduled_for).toLocaleString("en-GB")} — ~${data.estimated_recipients} recipients.`);
      } else {
        toast.success(`Sent to ${data.sent} of ${data.total} — ${data.failed ? `${data.failed} failed` : "all delivered"}.`);
      }
      resetForm();
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send email"); }
    finally { setSending(false); }
  };

  const cancelScheduled = async (h) => {
    if (!window.confirm(`Cancel the scheduled email "${h.subject}"?`)) return;
    try {
      await api.post(`/admin/email-history/${h.id}/cancel`);
      toast.success("Scheduled email cancelled.");
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  const fieldStyle = { width: "100%", padding: "12px 14px", borderRadius: 12, border: "1px solid var(--hairline)", background: "#fff", fontSize: ".95rem", fontFamily: "inherit", color: "var(--charcoal, #2B2622)" };
  const labelStyle = { display: "block", fontSize: ".8rem", color: "var(--taupe)", marginBottom: 6, fontWeight: 500 };

  return (
    <div data-testid="admin-email" style={{ maxWidth: 720 }}>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="od-eyebrow" style={{ marginBottom: 6 }}>Compose a branded email</div>
        <h3 style={{ margin: "0 0 4px", fontWeight: 500 }}>Send a typed message</h3>
        <p style={{ color: "var(--taupe)", margin: "0 0 20px", fontSize: ".9rem" }}>
          Your message is automatically wrapped in the Olivia Does branding and sent from your verified address.
        </p>

        <div style={{ display: "grid", gap: 16 }}>
          <div>
            <label style={labelStyle}>Send to</label>
            <select data-testid="email-recipient-type" value={recipientType} onChange={(e) => { setRecipientType(e.target.value); setPreview(null); }} style={fieldStyle}>
              <option value="customers">All customers ({groupCount.customers})</option>
              <option value="trades">All partners ({groupCount.trades})</option>
              <option value="everyone">Everyone ({groupCount.everyone})</option>
              <option value="single">One specific person…</option>
              <option value="custom">Type email address(es)…</option>
            </select>
          </div>

          {recipientType === "custom" && (
            <div>
              <label style={labelStyle}>Email address(es) <span style={{ fontWeight: 400 }}>— separate several with a comma or new line</span></label>
              <textarea data-testid="email-custom-addresses" value={customEmails} onChange={(e) => { setCustomEmails(e.target.value); setPreview(null); }} rows={3} placeholder={"lead@myjobquote.co.uk\nanother.lead@example.com"} style={{ ...fieldStyle, resize: "vertical", lineHeight: 1.5 }} />
              {customCount > 0 && <p style={{ color: "var(--taupe)", fontSize: ".78rem", margin: "6px 0 0" }}>{customCount} {customCount === 1 ? "address" : "addresses"} entered</p>}
            </div>
          )}

          {recipientType === "single" && (
            <div>
              <label style={labelStyle}>Recipient</label>
              <select data-testid="email-recipient-single" value={recipientId} onChange={(e) => setRecipientId(e.target.value)} style={fieldStyle}>
                <option value="">Choose a person…</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} · {p.email} ({p.role})</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label style={labelStyle}>Subject line</label>
            <input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. A little update from Olivia Does" style={fieldStyle} />
          </div>

          <div>
            <label style={labelStyle}>Heading <span style={{ fontWeight: 400 }}>(optional — defaults to the subject)</span></label>
            <input data-testid="email-heading" value={heading} onChange={(e) => setHeading(e.target.value)} placeholder="Big heading inside the email" style={fieldStyle} />
          </div>

          <div>
            <label style={labelStyle}>Message</label>
            <textarea data-testid="email-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={8} placeholder={"Type your message here.\n\nLeave a blank line between paragraphs."} style={{ ...fieldStyle, resize: "vertical", lineHeight: 1.5 }} />
          </div>

          <div className="ogrid cols-2" style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Button text <span style={{ fontWeight: 400 }}>(optional)</span></label>
              <input data-testid="email-cta-label" value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} placeholder="e.g. Book now" style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Button link</label>
              <input data-testid="email-cta-link" value={ctaLink} onChange={(e) => setCtaLink(e.target.value)} placeholder="https://oliviadoes.co.uk/book" style={fieldStyle} />
            </div>
          </div>

          <div style={{ borderTop: "1px solid var(--hairline)", paddingTop: 16 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: ".9rem", fontWeight: 500 }}>
              <input type="checkbox" data-testid="email-schedule-toggle" checked={scheduleMode} onChange={(e) => setScheduleMode(e.target.checked)} />
              Schedule for later instead of sending now
            </label>
            {scheduleMode && (
              <div style={{ marginTop: 12 }}>
                <label style={labelStyle}>Send date & time <span style={{ fontWeight: 400 }}>(UK time)</span></label>
                <input type="datetime-local" data-testid="email-schedule-datetime" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} style={{ ...fieldStyle, maxWidth: 280 }} />
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
            <button className="btn secondary" data-testid="email-preview" onClick={openPreview} disabled={previewLoading}>
              {previewLoading ? "Building…" : "Preview & recipients"}
            </button>
            <button className="btn secondary" data-testid="email-send-test" onClick={sendTest} disabled={sending}>
              {sending ? "Working…" : "Send test to me"}
            </button>
            <button className="btn rose" data-testid="email-send" onClick={sendReal} disabled={sending}>
              {sending ? "Working…" : scheduleMode ? "Schedule email" : "Send email"}
            </button>
          </div>
        </div>
      </div>

      {/* SEND HISTORY */}
      <div className="card" data-testid="email-history">
        <div className="od-eyebrow" style={{ marginBottom: 6 }}>Send history</div>
        <h3 style={{ margin: "0 0 4px", fontWeight: 500 }}>Emails you've sent</h3>
        <p style={{ color: "var(--taupe)", margin: "0 0 14px", fontSize: ".8rem" }}>
          Opens are approximate — some email apps block tracking or pre-load images, so the real number can be higher or lower.
        </p>
        {history.length === 0 ? (
          <p style={{ color: "var(--taupe)", margin: 0, fontSize: ".9rem" }}>Nothing sent yet — your first email will appear here.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }}>
              <thead><tr style={{ textAlign: "left", color: "var(--taupe)" }}>
                {["Date", "Subject", "Audience", "Status", "Delivered", "Opened", "Clicked", ""].map((h) => <th key={h} style={{ padding: "8px 10px", fontWeight: 500 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {history.map((h) => {
                  const st = STATUS_STYLE[h.status] || { bg: "#F0ECE9", color: "#8A817B", label: h.status };
                  const dateShown = h.status === "scheduled" ? h.scheduled_for : (h.sent_at || h.created_at);
                  return (
                    <tr key={h.id} data-testid={`email-history-row-${h.id}`} style={{ borderTop: "1px solid var(--hairline)" }}>
                      <td style={{ padding: "10px", whiteSpace: "nowrap", color: "var(--taupe)" }}>
                        {h.status === "scheduled" ? "→ " : ""}{new Date(dateShown).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td style={{ padding: "10px", fontWeight: 500 }}>{h.subject}</td>
                      <td style={{ padding: "10px", color: "var(--taupe)" }}>{h.audience_label}</td>
                      <td style={{ padding: "10px" }}>
                        <span style={{ background: st.bg, color: st.color, borderRadius: 999, padding: "3px 10px", fontSize: ".75rem", fontWeight: 500 }}>{st.label}</span>
                      </td>
                      <td style={{ padding: "10px", color: "var(--taupe)" }}>
                        {h.status === "sent" ? `${h.sent}${h.failed ? ` · ${h.failed} failed` : ""}` : h.status === "scheduled" ? `~${h.estimated_recipients ?? "—"}` : "—"}
                      </td>
                      <td style={{ padding: "10px", color: "var(--taupe)" }} data-testid={`email-opens-${h.id}`}>
                        {h.status === "sent" && h.sent ? `${h.unique_opens || 0} (${Math.round(((h.unique_opens || 0) / h.sent) * 100)}%)` : "—"}
                      </td>
                      <td style={{ padding: "10px", color: "var(--taupe)" }} data-testid={`email-clicks-${h.id}`}>
                        {h.status === "sent" && h.sent && h.cta_link ? `${h.unique_clicks || 0} (${Math.round(((h.unique_clicks || 0) / h.sent) * 100)}%)` : "—"}
                      </td>
                      <td style={{ padding: "10px", textAlign: "right" }}>
                        {h.status === "scheduled" && (
                          <button data-testid={`email-cancel-${h.id}`} onClick={() => cancelScheduled(h)} style={{ color: "#B04A4A", border: "1px solid #E9CFCF", borderRadius: 999, padding: "3px 12px", fontSize: ".78rem", background: "transparent", cursor: "pointer" }}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PREVIEW MODAL */}
      {preview && (
        <div data-testid="email-preview-modal" onClick={() => setPreview(null)} style={{ position: "fixed", inset: 0, background: "rgba(43,38,34,.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 18, maxWidth: 600, width: "100%", maxHeight: "88vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--hairline)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600 }}>Email preview</div>
                <div data-testid="email-preview-count" style={{ color: "var(--taupe)", fontSize: ".85rem", marginTop: 2 }}>
                  This will go to <b>{preview.count}</b> {preview.count === 1 ? "person" : "people"}{recipientType !== "single" ? " (opted-out excluded)" : ""}.
                </div>
              </div>
              <button data-testid="email-preview-close" onClick={() => setPreview(null)} style={{ border: "none", background: "transparent", fontSize: "1.4rem", cursor: "pointer", color: "var(--taupe)", lineHeight: 1 }}>×</button>
            </div>
            <iframe title="email-preview" srcDoc={preview.html} style={{ border: "none", width: "100%", flex: 1, minHeight: 420, background: "#FBF6F1" }} />
          </div>
        </div>
      )}
    </div>
  );
}
